import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, Clock, XCircle, MapPin, Calendar, CreditCard, Loader2, ShieldCheck, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OfferPage() {
  const { offerId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    const loadOffer = async () => {
      try {
        const res = await fetch(`${API}/api/offers/${offerId}?token=${token}`);
        if (res.ok) setOffer(await res.json());
        else setError('Offerte nicht gefunden oder ungültiger Link.');
      } catch (e) { setError('Netzwerkfehler.'); }
      setLoading(false);
    };
    if (offerId && token) loadOffer();
    else { setError('Ungültiger Link.'); setLoading(false); }
  }, [offerId, token]);

  const handleCheckout = async () => {
    setCheckingOut(true);
    try {
      const res = await fetch(`${API}/api/offers/${offerId}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        toast.error('Keine Checkout-URL erhalten');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler beim Starten der Zahlung');
      }
    } catch (e) { toast.error('Netzwerkfehler'); }
    setCheckingOut(false);
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="spinner" /></div>;
  if (error) return (
    <div className="flex items-center justify-center min-h-screen px-4">
      <Card className="max-w-md w-full"><CardContent className="py-12 text-center"><XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" /><h2 className="text-xl font-bold mb-2">Fehler</h2><p className="text-[hsl(var(--muted-foreground))]">{error}</p></CardContent></Card>
    </div>
  );

  if (offer.status === 'paid') return (
    <div className="flex items-center justify-center min-h-screen px-4">
      <Card className="max-w-md w-full"><CardContent className="py-12 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Offerte bezahlt</h2>
        <p className="text-[hsl(var(--muted-foreground))] mb-4">Diese Offerte wurde bereits bezahlt. Ihre Buchung ist aktiv.</p>
        <Button onClick={() => window.location.href = '/my-bookings'} data-testid="goto-bookings-btn">Zu meinen Buchungen</Button>
      </CardContent></Card>
    </div>
  );

  if (offer.status === 'accepted') return (
    <div className="flex items-center justify-center min-h-screen px-4">
      <Card className="max-w-md w-full"><CardContent className="py-12 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Offerte angenommen</h2>
        <p className="text-[hsl(var(--muted-foreground))] mb-4">Diese Offerte wurde bereits angenommen.</p>
        <Button onClick={() => window.location.href = '/'}>Zur Startseite</Button>
      </CardContent></Card>
    </div>
  );

  const total = (offer.price || 0) + (offer.services_price || 0);
  const canPay = offer.status === 'sent';

  return (
    <div className="min-h-screen bg-[hsl(var(--surface-2))] py-8 px-4" data-testid="offer-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 bg-[hsl(var(--primary))] rounded-lg flex items-center justify-center text-white font-bold text-sm">ZO</div>
            <span className="font-semibold text-lg">ZO Werbefläche</span>
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Ihre Offerte</h1>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Offerte für {offer.customer_name}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                offer.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                offer.status === 'accepted' ? 'bg-green-100 text-green-700' :
                offer.status === 'expired' ? 'bg-orange-100 text-orange-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {offer.status === 'sent' ? 'Offen' : offer.status === 'accepted' ? 'Angenommen' : offer.status === 'expired' ? 'Abgelaufen' : offer.status}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Location */}
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[hsl(var(--primary))] mt-0.5" />
              <div>
                <p className="font-semibold">{offer.location?.name}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{offer.location?.address}</p>
              </div>
            </div>

            {/* Period */}
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[hsl(var(--primary))] mt-0.5" />
              <div>
                <p className="font-semibold">Zeitraum</p>
                <p className="text-sm">{new Date(offer.start_date).toLocaleDateString('de-CH')} – {new Date(offer.end_date).toLocaleDateString('de-CH')}</p>
              </div>
            </div>

            {/* Services */}
            {offer.service_details && offer.service_details.length > 0 && (
              <div className="bg-[hsl(var(--surface-2))] rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Zusatzleistungen</p>
                {offer.service_details.map(s => (
                  <div key={s.id} className="flex justify-between text-sm">
                    <span>{s.name}</span>
                    <span>CHF {s.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Price */}
            <div className="bg-[hsl(var(--surface-2))] rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span>Grundpreis</span>
                <span className="font-semibold">CHF {offer.price?.toFixed(2)}</span>
              </div>
              {offer.services_price > 0 && (
                <div className="flex justify-between items-center mb-2">
                  <span>Zusatzleistungen</span>
                  <span>CHF {offer.services_price?.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between items-center">
                <span className="font-bold text-lg">Total</span>
                <span className="font-bold text-2xl text-[hsl(var(--primary))]">CHF {total.toFixed(2)}</span>
              </div>
            </div>

            {/* Notes */}
            {offer.notes && (
              <div className="bg-[hsl(var(--surface-2))] rounded-lg p-4">
                <p className="text-sm font-medium mb-1">Anmerkungen</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] italic">{offer.notes}</p>
              </div>
            )}

            {/* Validity */}
            {offer.valid_until && (
              <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                <Clock className="w-4 h-4" />
                <span>Gültig bis: {new Date(offer.valid_until).toLocaleDateString('de-CH')}</span>
              </div>
            )}

            {/* PDF Download */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.open(`${API}/api/offers/${offerId}/pdf?token=${token}`, '_blank')}
              data-testid="download-offer-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Offerte als PDF herunterladen
            </Button>

            {/* Pay Button */}
            {canPay && (
              <div className="space-y-3">
                <Button
                  className="w-full h-14 text-lg"
                  onClick={handleCheckout}
                  disabled={checkingOut}
                  data-testid="pay-offer-btn"
                >
                  {checkingOut ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Weiterleitung zu Stripe...</>
                  ) : (
                    <><CreditCard className="w-5 h-5 mr-2" />Offerte annehmen & bezahlen</>
                  )}
                </Button>
                <div className="flex items-center justify-center gap-4 text-xs text-[hsl(var(--muted-foreground))]">
                  <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Sichere Zahlung via Stripe</span>
                  <span className="flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" />Kreditkarte / TWINT</span>
                </div>
              </div>
            )}

            {offer.status === 'expired' && (
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <XCircle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <p className="font-semibold text-orange-700">Diese Offerte ist leider abgelaufen.</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Kontaktieren Sie uns für eine neue Offerte.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">
          ZO Werbeflächen | Bahnhofstrasse 75, 8620 Wetzikon | +41 79 696 98 99
        </p>
      </div>
    </div>
  );
}
